import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MOCK_VESSELS, MOCK_CREW, resolveClientKey, getClientVessels, getClientCrew } from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { Siren, Phone, Mail, Clock, Download } from "lucide-react";
import { useState } from "react";

const emergencyContacts = [
  { name: "Velocity Offshore 24/7 Ops Line", phone: "+1 (713) 555-0100", email: "ops@velocityoffshore.com", availability: "24/7" },
  { name: "MRCC Georgetown (Guyana)", phone: "+592 226-1234", email: "mrcc@guyana.gov.gy", availability: "24/7" },
  { name: "MRCC Luanda (Angola)", phone: "+244 222-310-200", email: "mrcc@angola.gov.ao", availability: "24/7" },
  { name: "Client Emergency Coordinator", phone: "+1 (832) 555-0200", email: "emergency@clienta.com", availability: "24/7" },
  { name: "MEDIVAC Provider — Global Response", phone: "+1 (281) 555-0300", email: "dispatch@globalresponse.com", availability: "24/7" },
];

const emergencyLog = [
  { id: "e1", date: "2025-02-14", vessel: "Velocity Pioneer", type: "Medical", actions: "Crew member with chest pain. MEDIVAC arranged. Helicopter transfer to Georgetown hospital.", outcome: "Crew member recovered. Returned to duty March 5.", status: "Closed" },
  { id: "e2", date: "2025-01-22", vessel: "Velocity Benguela", type: "Equipment", actions: "Engine room fire alarm. Crew response per fire plan. Fire contained and extinguished.", outcome: "No injuries. Minor smoke damage. Repairs completed Jan 28.", status: "Closed" },
];

const EmergencySAR = () => {
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientVessels = clientKey ? getClientVessels(clientKey) : MOCK_VESSELS;
  const clientCrew = clientKey ? getClientCrew(clientKey) : MOCK_CREW;
  const [selectedVessel, setSelectedVessel] = useState(clientVessels[0]?.name || "");
  const vesselCrew = clientCrew.filter(c => c.vessel === selectedVessel);

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Safety</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Emergency & SAR</h1>
      </div>

      <Tabs defaultValue="contacts" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="contacts">Emergency Contacts</TabsTrigger>
          <TabsTrigger value="sar">SAR Plan</TabsTrigger>
          <TabsTrigger value="log">Emergency Log</TabsTrigger>
          <TabsTrigger value="muster">Muster List</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {emergencyContacts.map(c => (
              <div key={c.name} className="portal-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Siren className="w-5 h-5 text-[#DC3545]" />
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{c.availability}</span>
                </div>
                <h3 className="font-bold text-sm mb-3">{c.name}</h3>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs"><Phone className="w-3.5 h-3.5 text-[hsl(215,12%,50%)]" /><span>{c.phone}</span></div>
                  <div className="flex items-center gap-2 text-xs"><Mail className="w-3.5 h-3.5 text-[hsl(215,12%,50%)]" /><span className="text-[hsl(211,100%,40%)]">{c.email}</span></div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sar">
          <div className="portal-card p-6 text-center">
            <Select value={selectedVessel} onValueChange={setSelectedVessel}>
              <SelectTrigger className="w-[220px] mx-auto mb-4"><SelectValue /></SelectTrigger>
              <SelectContent>{MOCK_VESSELS.map(v => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="p-8 bg-[hsl(220,20%,97%)] rounded-lg">
              <Siren className="w-12 h-12 text-[hsl(215,12%,50%)]/30 mx-auto mb-3" />
              <p className="text-sm text-[hsl(215,12%,50%)]">SAR / Medical Emergency Plan for <strong>{selectedVessel}</strong></p>
              <div className="flex justify-center gap-3 mt-4">
                <button className="px-4 py-2 text-xs font-medium rounded-lg bg-[hsl(211,100%,40%)] text-white hover:bg-[hsl(211,100%,35%)]"><Download className="w-3.5 h-3.5 inline mr-1" />Download PDF</button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="log">
          <div className="portal-card overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                {["Date", "Vessel", "Type", "Actions Taken", "Outcome", "Status"].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {emergencyLog.map(e => (
                  <tr key={e.id} className="border-b border-[hsl(220,15%,92%)]">
                    <td className="px-4 py-2.5">{e.date}</td><td className="px-4 py-2.5">{e.vessel}</td>
                    <td className="px-4 py-2.5 font-medium">{e.type}</td>
                    <td className="px-4 py-2.5 max-w-[200px] text-[hsl(215,12%,50%)]">{e.actions}</td>
                    <td className="px-4 py-2.5 max-w-[200px] text-[hsl(215,12%,50%)]">{e.outcome}</td>
                    <td className="px-4 py-2.5"><span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">{e.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="muster">
          <div className="mb-4">
            <Select value={selectedVessel} onValueChange={setSelectedVessel}>
              <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>{MOCK_VESSELS.map(v => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="portal-card overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                {["Crew Member", "Role", "Emergency Station", "Duties"].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {vesselCrew.map((c, i) => (
                  <tr key={c.id} className="border-b border-[hsl(220,15%,92%)]">
                    <td className="px-4 py-2.5 font-medium">{c.name}</td>
                    <td className="px-4 py-2.5">{c.role}</td>
                    <td className="px-4 py-2.5">{i === 0 ? "Bridge" : i === 1 ? "Liferaft Station" : "Muster Point A"}</td>
                    <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{i === 0 ? "Overall command, distress comms" : i === 1 ? "Liferaft deployment, headcount" : "Assist passengers, fire party"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default EmergencySAR;
